import { useState, useCallback } from "react";
import {
  updateUserOnBehalf,
  createContact,
  inviteToEvent,
  setUserRole,
  deleteUser,
  deleteContact,
  removeFromEvent,
} from "../services/userManagementService";
import type {
  UpdateUserOnBehalfInput,
  CreateContactInput,
  InviteToEventInput,
  InviteToEventResponse,
  RemoveFromEventInput,
  RemoveFromEventResponse,
  Role,
} from "@event-planner/shared";

export function useUpdateUserOnBehalf() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(async (input: UpdateUserOnBehalfInput) => {
    setLoading(true);
    setError(null);
    try {
      const result = await updateUserOnBehalf(input);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Update user failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { update, loading, error };
}

export function useCreateContact() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (input: CreateContactInput) => {
    setLoading(true);
    setError(null);
    try {
      const result = await createContact(input);
      return result;
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Create contact failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error };
}

export function useInviteToEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const invite = useCallback(
    async (input: InviteToEventInput): Promise<InviteToEventResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await inviteToEvent(input);
        return result;
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Invite to event failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { invite, loading, error };
}

export function useSetUserRole() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const changeRole = useCallback(
    async (targetUserId: string, role: Role) => {
      setLoading(true);
      setError(null);
      try {
        const result = await setUserRole({ targetUserId, role });
        return result;
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Set user role failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { changeRole, loading, error };
}

export function useDeleteUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await deleteUser({ userId });
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Delete user failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}

/**
 * Cascade-delete a pre-claim contact. Used by the UserDetailPage when
 * the row is a contact (no Auth account yet). Mirrors useDeleteUser
 * but routes through the deleteContact callable.
 */
export function useDeleteContact() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (contactId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await deleteContact({ contactId });
      return result;
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Delete contact failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}

/**
 * Route a person delete to the right cascade callable. `deleteUser` is
 * keyed by the Auth uid and `deleteContact` by the personId (ADR-136);
 * the merged deletePerson callable the ADR sketched never shipped. The
 * person page holds the personId, so this hook decides on claim state:
 * claimed (authUid set) goes to deleteUser with the uid, unclaimed goes
 * to deleteContact with the personId.
 */
export function useDeletePerson() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(
    async (person: { id: string; authUid: string | null }) => {
      setLoading(true);
      setError(null);
      try {
        return person.authUid
          ? await deleteUser({ userId: person.authUid })
          : await deleteContact({ contactId: person.id });
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Delete person failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { remove, loading, error };
}

/**
 * Hook for the unified remove-from-event callable (ADR-089). Handles both
 * pending invitations and active members; the input shape lets the caller
 * pass whichever identifier is on hand (`invitationId` for pending rows,
 * `userId` for active rows). Replaces the former `useRevokeInvitation` and
 * the inline `removeUserFromEvent` callable invocations.
 */
export function useRemoveFromEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(
    async (input: RemoveFromEventInput): Promise<RemoveFromEventResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await removeFromEvent(input);
        return result;
      } catch (err) {
        const e =
          err instanceof Error
            ? err
            : new Error("Remove from event failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { remove, loading, error };
}
